import type { DistanceTreeItem } from 'common/components/Elements/DistanceTree/DistanceTree';
import { generateRandomNumber } from 'common/utilities/core/number';
import type { BinnedPointToPosition } from 'dataAnalyzer/components/ChartView/ChartView';
import type {
  DistanceTreeNode,
} from 'sequenceAnalyser/api/sequenceAnalyserApiClient';

export function calcLongestDistance(node: DistanceTreeNode, distance: number): number {
  const nodeDistance = node.distance === undefined ? distance : node.distance + distance;

  const leftDistance = node.left?.distance !== undefined
    ? calcLongestDistance(node.left, nodeDistance)
    : 0;

  const rightDistance = node.right?.distance !== undefined
    ? calcLongestDistance(node.right, nodeDistance)
    : 0;

  return Math.max(nodeDistance, leftDistance, rightDistance);
}

let count = 0;

export function distanceTreeItemsFromDistanceTreeNode(
  node: DistanceTreeNode,
  parentId: number | undefined,
  /** longest distance for a leaf to root in absolute value (i.e. 200) */
  longestDistance: number,
  /** last distance in aggregate absolute value (i.e. 50 + 50 = 100 out of 200) */
  lastAggDistance: number,
  /** last distance in absolute value (i.e. 50) */
  lastDistance: number | undefined,
  /** distance between leafs within the tree in pixels (i.e. root is 0%, 100 out of 200 is 50%, longest leaf is 100%) */
  leafDistance: number,
  /** mapping of ids to x/y positions */
  idToPosition?: BinnedPointToPosition,
): DistanceTreeItem[] {
  if (!parentId) count = 0;
  const distanceAsPercentage = parentId === undefined ? 0 : ((100 * lastAggDistance) / longestDistance);

  const item: DistanceTreeItem = {
    id: node.id === undefined ? generateRandomNumber() : node.id,
    parentId,
    distance: distanceAsPercentage,
    nodeDistance: lastDistance || 0,
  };

  if (node.id !== undefined && idToPosition) {
    item.position = idToPosition[node.id.toString()];
  }

  if (!item.position) {
    item.position = node.id ? (count * leafDistance) + leafDistance / 2 : undefined;
  }

  const newAggDistance = lastAggDistance + (node.distance === undefined ? 0 : node.distance);
  const newLastDistance = node.distance || lastDistance;

  const leftItems = node.left
    ? distanceTreeItemsFromDistanceTreeNode(
      node.left, item?.id || parentId, longestDistance, newAggDistance, newLastDistance, leafDistance, idToPosition,
    )
    : [];
  const rightItems = node.right
    ? distanceTreeItemsFromDistanceTreeNode(
      node.right, item?.id || parentId, longestDistance, newAggDistance, newLastDistance, leafDistance, idToPosition,
    )
    : [];

  if (node.id) count += 1;

  let newItems = [];
  if (item) newItems.push(item);
  newItems = [...newItems, ...leftItems, ...rightItems];

  return newItems;
}
